import { zodResolver } from '@hookform/resolvers/zod';
import { getTomorrow, isNotNullOrUndefined, vyDate } from '@vy/utils';
import { useForm, FormProvider } from 'react-hook-form';
import { ErrorLabel } from '../../../components/design/ErrorLabel';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useContract } from '../../../hooks/requests/use-contract';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import type { NewPlanForm } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { getCreatePlanDefaultValues } from '../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';
import { RevisePlanInputFormSchema } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/revise-plan-input-form';
import { useContractRevisePagePlanCreateReason } from './contract-revise-page-hooks/use-contract-revise-page-plan-create-reason';
import { PLAN_CREATION_REASONS } from '../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';

const ContractReviseForm: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { contractId } = useValidatedParams('contractId');
  const {
    data: { contract },
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const reviseReason = useContractRevisePagePlanCreateReason(contractId);
  const isReviseCurrentContract = reviseReason === PLAN_CREATION_REASONS.AddPhaseToCurrentContract;

  const contractNewStartDate = getTomorrow(vyDate(contract.endDate));
  const startDate = isReviseCurrentContract
    ? contract.startDate
    : contractNewStartDate.startOf('day').toDate();

  const formMethods = useForm<PlanInputForm, unknown, NewPlanForm>({
    resolver: zodResolver(RevisePlanInputFormSchema),
    defaultValues: getCreatePlanDefaultValues({
      plan: contract.plan,
      startDate,
      customerId: contract.customer.id,
      shouldUnsetProductDates: false,
    }),
  });

  return (
    <FormProvider {...formMethods}>
      <form className="flex h-full flex-col">{children}</form>
      {isNotNullOrUndefined(formMethods.formState.errors.root) && (
        <ErrorLabel errorMessage={formMethods.formState.errors.root?.message ?? ''} />
      )}
    </FormProvider>
  );
};

const ContractReviseFormWithSuspenseBoundary = withSuspenseBoundary(ContractReviseForm);
export { ContractReviseFormWithSuspenseBoundary as ContractReviseForm };
